// ---------------------------------------------------------------------------
// Marks, as div compositions.
//
// These five are not icons and `Icon.tsx` is not where they belong. Each is
// built from divs at the geometry the handoff specifies and scales from that
// geometry rather than from a font size, so a crown at 14px keeps the same
// silhouette as a crown at 20px, and each is drawn at the weight and corner
// radius of the tiles it sits beside. A Lucide crown at 14px is a different
// object, and swapping one in makes the seat rows stop matching the board.
//
// Everything that is an ordinary interface icon — a menu, a bin, a tick — goes
// through `Icon.tsx` and the Lucide set instead. The test is whether the mark
// has to agree with the tiles: these five do, a bin does not.
//
// Colour comes from `currentColor` wherever a glyph appears in more than one
// context, so a caller sets it with a text utility.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';

/** Five-point crown, per the handoff's clip path. Host marker. */
export function Crown({ size = 20, className }: { size?: number; className?: string }): ReactElement {
  return (
    <div
      aria-hidden="true"
      className={`shrink-0 bg-accent ${className ?? ''}`}
      style={{
        width: size,
        height: (size * 15) / 20,
        clipPath: 'polygon(0 100%, 0 30%, 25% 55%, 50% 0, 75% 55%, 100% 30%, 100% 100%)',
      }}
    />
  );
}

/**
 * Shackle above a body, sized off the 19px body in the handoff. Used on a
 * locked tile and beside a locked seat.
 */
export function Padlock({ size = 19, className }: { size?: number; className?: string }): ReactElement {
  const scale = size / 19;
  return (
    <div
      aria-hidden="true"
      className={`flex shrink-0 flex-col items-center ${className ?? ''}`}
      style={{ color: 'inherit' }}
    >
      <div
        style={{
          width: 12 * scale,
          height: 8 * scale,
          border: `${Math.max(1, 2 * scale)}px solid currentColor`,
          borderBottom: 'none',
          borderRadius: `${7 * scale}px ${7 * scale}px 0 0`,
        }}
      />
      <div
        style={{
          width: 19 * scale,
          height: 14 * scale,
          borderRadius: 2 * scale,
          background: 'currentColor',
        }}
      />
    </div>
  );
}

/** Stem, head, two eyes. Marks a bot seat, alongside its difficulty. */
export function RobotGlyph({ size = 14 }: { size?: number }): ReactElement {
  const scale = size / 14;
  return (
    <div aria-hidden="true" className="flex shrink-0 flex-col items-center" style={{ gap: 1 * scale }}>
      <div style={{ width: 2 * scale, height: 3 * scale, background: 'currentColor' }} />
      <div
        className="flex items-center justify-center"
        style={{
          width: 14 * scale,
          height: 11 * scale,
          borderRadius: 3 * scale,
          border: `${Math.max(1, 1.5 * scale)}px solid currentColor`,
          gap: 3 * scale,
        }}
      >
        <div style={{ width: 2 * scale, height: 2 * scale, borderRadius: '50%', background: 'currentColor' }} />
        <div style={{ width: 2 * scale, height: 2 * scale, borderRadius: '50%', background: 'currentColor' }} />
      </div>
    </div>
  );
}

/**
 * Three ascending bars. The level is the connection reading, and the colour
 * carries it as well as the height does, because two bars and three bars are
 * hard to tell apart at a glance on a phone.
 */
export function SignalBars({ level, className }: { level: 1 | 2 | 3; className?: string }): ReactElement {
  const tone = level === 3 ? 'bg-good' : level === 2 ? 'bg-accent' : 'bg-alert';
  return (
    <div aria-hidden="true" className={`flex items-end gap-[2px] ${className ?? ''}`} data-signal-level={level}>
      {[6, 10, 13].map((height, index) => (
        <div
          key={height}
          className={`w-[3px] ${index < level ? tone : 'bg-line-strong'}`}
          style={{ height }}
        />
      ))}
    </div>
  );
}

/**
 * Two bars, dropped into the centre of a frozen clock. It reads as stopped from
 * across the room, which is the whole point of the frozen variant.
 */
export function PauseGlyph({ size = 16 }: { size?: number }): ReactElement {
  return (
    <div aria-hidden="true" className="flex items-center" style={{ gap: size * 0.19 }}>
      <div className="bg-ink" style={{ width: size * 0.25, height: size }} />
      <div className="bg-ink" style={{ width: size * 0.25, height: size }} />
    </div>
  );
}
